import {
  DASHBOARD_RANGE_KEYS,
  DEFAULT_DASHBOARD_RANGE,
  dashboardRange,
  isCalendarDate,
  type DashboardRangeKey,
  type DateRange,
} from '@promanage/core';

/** What the dashboard's URL carries. Everything absent means the server's default range. */
export interface DashboardSearch {
  /** A preset, resolved server-side against the NZ day. See `range.ts` in @promanage/core. */
  range?: DashboardRangeKey;
  /** Two typed dates. Both or neither, and they beat `range` if somebody sends all three. */
  from?: string;
  to?: string;
}

export function isRangeKey(value: string): value is DashboardRangeKey {
  return (DASHBOARD_RANGE_KEYS as readonly string[]).includes(value);
}

/**
 * The period out of the query string.
 *
 * SHAPE-CHECKED ONLY. A backwards range is refused by `dashboardSummarySchema`, which is the
 * boundary that also sees the hand-edited URL and the stale bookmark; a second copy of that rule
 * here would be a second place for it to drift, and what the operator needs is the server's
 * sentence rather than a router error page. Anything unrecognised is DROPPED rather than rejected
 * — `?range=lastmonth` from a typo or an old link should open the dashboard on its default, not
 * refuse to render it.
 *
 * CALLED TWICE, AND IT HAS TO BE. This is the route's `validateSearch`, which types the search for
 * `navigate` — but the router does NOT strip the keys it drops: `validateSearch` returning `{}`
 * for `?range=lastmonth` still leaves `Route.useSearch()` handing back `{ range: 'lastmonth' }`.
 * Verified in the browser, not guessed. So the typed `DashboardSearch` a component receives is a
 * compile-time promise the runtime does not keep, and everything downstream — `resolveRange`, and
 * the input this page sends the API — parses through here first. Cheap, and the alternative is a
 * white screen from a query-string typo.
 */
export function dashboardSearch(search: Readonly<Record<string, unknown>>): DashboardSearch {
  return {
    ...(typeof search.range === 'string' && isRangeKey(search.range)
      ? { range: search.range }
      : {}),
    ...(typeof search.from === 'string' && isCalendarDate(search.from)
      ? { from: search.from }
      : {}),
    ...(typeof search.to === 'string' && isCalendarDate(search.to) ? { to: search.to } : {}),
  };
}

/**
 * The period the URL is asking about, resolved locally.
 *
 * THE SERVER RESOLVES IT TOO and its answer is authoritative — the figures were computed from it,
 * and every heading on the page is written from what the payload echoes back. This exists for two
 * smaller jobs: giving the bar dates to show on the first paint rather than flickering them in
 * half a second later, and telling whether what is on screen answers the period the URL currently
 * names, which is what dims the trends row while a new one loads.
 *
 * CLAMPED THE SAME WAY THE SERVER CLAMPS IT, to today. Both sides call the same pure function on
 * the same New Zealand day, so they agree on every preset; without this they would disagree
 * forever on a range typed to end next month, and that row would sit dimmed for good.
 *
 * ONE END ON ITS OWN IS IGNORED, matching the contract: a half-open range would have to be
 * completed somewhere, and both candidates put a window on screen nobody asked for.
 *
 * ITS INPUT IS RE-PARSED rather than trusted, for the reason `dashboardSearch` gives above: the
 * router hands a component whatever was in the URL, `validateSearch` or no `validateSearch`. Take
 * `search.range` on trust and `?range=lastmonth` reaches `dashboardRange`, falls through a switch
 * that TypeScript believes is exhaustive, and returns undefined into `.from` — a blank dashboard
 * with a stack trace on it, from a typo in a link.
 */
export function resolveRange(search: DashboardSearch, today: string): DateRange {
  const asked = dashboardSearch({ ...search });

  const range =
    asked.from !== undefined && asked.to !== undefined
      ? { from: asked.from, to: asked.to }
      : dashboardRange(asked.range ?? DEFAULT_DASHBOARD_RANGE, today);

  return { from: range.from, to: range.to > today ? today : range.to };
}
